import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../services/setupClientSubmaster/apiCliente";

type DeliveryOption = {
  id: number;
  kind: "TYPE" | "REASON";
  label: string;
  requiresReason: boolean;
  active: boolean;
};

const accessKey = "chavedeacessomaster#2528";

function DeliveryOptionsTable() {
  const [options, setOptions] = useState<DeliveryOption[]>([]);
  const [kind, setKind] = useState<"TYPE" | "REASON">("TYPE");
  const [label, setLabel] = useState("");
  const [requiresReason, setRequiresReason] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const response = await apiClient().post("/internal/deliveryOptions", { accessKey });
    if (!Array.isArray(response?.data)) throw new Error("Não foi possível carregar as opções.");
    setOptions(response.data);
  }, []);

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, [load]);

  const reset = () => {
    setEditingId(null);
    setLabel("");
    setRequiresReason(false);
  };

  const save = async (option?: DeliveryOption) => {
    const value = option?.label ?? label.trim();
    if (!value) { setError("Informe a descrição."); return; }
    setBusy(true);
    setError("");
    try {
      const response = await apiClient().post("/internal/saveDeliveryOption", {
        accessKey,
        id: option?.id ?? editingId,
        kind: option?.kind ?? kind,
        label: value,
        requiresReason: option?.requiresReason ?? requiresReason,
        active: option ? !option.active : (options.find((item) => item.id === editingId)?.active ?? true),
      });
      if (!response?.data?.id) throw new Error(response?.data?.message || "Não foi possível salvar a opção.");
      reset();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Não foi possível salvar a opção.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mb-4 p-4 rounded" style={{ background: "#f8fbff", border: "1px solid #dbe7f5" }}>
      <h3 className="h5 fw-bold">Tipos e motivos de entrega de EPI</h3>
      <p className="text-muted">Estas opções são usadas no app pelas empresas com a permissão ativa. Desativar uma opção preserva as entregas já registradas.</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="row g-2 align-items-end mb-4">
        <div className="col-12 col-md-3">
          <label className="form-label" htmlFor="delivery-kind">Categoria</label>
          <select id="delivery-kind" className="form-select" value={kind} onChange={(e) => { setKind(e.target.value as "TYPE" | "REASON"); setRequiresReason(false); }}>
            <option value="TYPE">Tipo</option><option value="REASON">Motivo</option>
          </select>
        </div>
        <div className="col-12 col-md-5">
          <label className="form-label" htmlFor="delivery-label">Descrição</label>
          <input id="delivery-label" className="form-control" maxLength={100} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex.: Troca" />
        </div>
        {kind === "TYPE" && <div className="col-12 col-md-2 form-check ps-4">
          <input id="delivery-reason-required" className="form-check-input" type="checkbox" checked={requiresReason} onChange={(e) => setRequiresReason(e.target.checked)} />
          <label htmlFor="delivery-reason-required" className="form-check-label">Exigir motivo</label>
        </div>}
        <div className="col-12 col-md-2 d-flex gap-2">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => save()}>{editingId ? "Salvar" : "Adicionar"}</button>
          {editingId && <button type="button" className="btn btn-outline-secondary" onClick={reset}>Cancelar</button>}
        </div>
      </div>
      {(["TYPE", "REASON"] as const).map((section) => (
        <div key={section} className="mb-3">
          <h4 className="h6 fw-bold">{section === "TYPE" ? "Tipos de entrega" : "Motivos de troca"}</h4>
          <div className="table-responsive"><table className="table table-sm table-striped align-middle" style={{ minWidth: 760 }}>
            <thead><tr><th>Descrição</th><th>Exige motivo</th><th>Status</th><th style={{ width: 230 }}>Ações</th></tr></thead>
            <tbody>{options.filter((item) => item.kind === section).map((item) => <tr key={item.id}>
              <td>{item.label}</td><td>{section === "TYPE" ? (item.requiresReason ? "Sim" : "Não") : "—"}</td>
              <td>{item.active ? "Ativo" : "Inativo"}</td>
              <td style={{ width: 230, whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", alignItems: "center", columnGap: 8 }}>
                  <button type="button" className="btn btn-sm btn-outline-primary" style={{ flexShrink: 0, minWidth: 72 }} onClick={() => { setEditingId(item.id); setKind(item.kind); setLabel(item.label); setRequiresReason(item.requiresReason); }}>Editar</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" style={{ flexShrink: 0, minWidth: 98 }} disabled={busy} onClick={() => save(item)}>{item.active ? "Desativar" : "Ativar"}</button>
                </div>
              </td>
            </tr>)}</tbody>
          </table></div>
        </div>
      ))}
    </section>
  );
}

export default DeliveryOptionsTable;
